import React, { Component } from "react";
import { connect } from "react-redux";
import { Table, Tag, Button, Card, Row, Col, Typography, Select, Input, DatePicker, message, Spin, Alert } from "antd";
import {
  fetchKhalesBalance,
  fetchCardOrders,
  fetchCardOrder,
  recheckCardUnit,
  resolveCardUnit
} from "../../Graphs/Card/khalesCardsAPI";
import CardOrderDetails from "./CardOrderDetails";

const { Title, Text } = Typography;
const { Option } = Select;

export class CardOrders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      balance: { balance_lyd: "0.000", balance_usd: "0.000" },
      orders: [],
      totalCount: 0,
      page: 1,
      limit: 20,
      filterStatus: undefined,
      filterUserId: "",
      filterDateFrom: null,
      filterDateTo: null,
      selectedOrder: null,
      isDetailVisible: false,
    };
  }

  componentDidMount() {
    const role = this.getRole();
    if (role === "Admin") {
      this.loadBalance();
      this.loadOrders();
    }
  }

  getRole = () => {
    const user = this.props.loginUserData || {};
    return user.role || "";
  };

  loadBalance = async () => {
    try {
      const res = await fetchKhalesBalance(this.props.authToken);
      if (res && res.success && res.data) {
        this.setState({ balance: res.data });
      }
    } catch (_) {}
  };

  loadOrders = async (pageOverride) => {
    try {
      this.setState({ isLoading: true });
      const p = pageOverride || this.state.page;
      const params = {
        page: p,
        limit: this.state.limit,
      };
      if (this.state.filterStatus) params.status = this.state.filterStatus;
      if (this.state.filterUserId) params.userId = this.state.filterUserId.trim();
      if (this.state.filterDateFrom) params.dateFrom = this.state.filterDateFrom;
      if (this.state.filterDateTo) params.dateTo = this.state.filterDateTo;

      const res = await fetchCardOrders(params, this.props.authToken);
      if (res && res.success && res.data) {
        this.setState({
          orders: res.data.orders || [],
          totalCount: res.data.count || 0,
          page: p,
        });
      }
    } catch (err) {
      message.error(err.message || "Failed to load card orders");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleOpenDetail = async (orderId) => {
    try {
      this.setState({ isLoading: true });
      const res = await fetchCardOrder(orderId, this.props.authToken);
      if (res && res.success && res.data) {
        this.setState({
          selectedOrder: res.data,
          isDetailVisible: true,
        });
      }
    } catch (err) {
      message.error(err.message || "Failed to load order detail");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleCloseDetail = () => {
    this.setState({
      selectedOrder: null,
      isDetailVisible: false,
    });
  };

  handleRecheck = async (unitId) => {
    try {
      this.setState({ isLoading: true });
      const res = await recheckCardUnit(unitId, {}, this.props.authToken);
      if (res && res.success) {
        message.success(res.message || "Unit rechecked successfully");
        if (this.state.selectedOrder) {
          await this.handleOpenDetail(this.state.selectedOrder.id);
        }
        await this.loadOrders();
      } else {
        message.error(res?.message || "Recheck did not resolve unit");
      }
    } catch (err) {
      message.error(err.message || "Failed to recheck unit");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleResolve = async (unitId, payload) => {
    try {
      this.setState({ isLoading: true });
      const res = await resolveCardUnit(unitId, payload, this.props.authToken);
      if (res && res.success) {
        message.success(res.message || "Unit resolved successfully");
        if (this.state.selectedOrder) {
          await this.handleOpenDetail(this.state.selectedOrder.id);
        }
        await this.loadOrders();
      } else {
        message.error(res?.message || "Resolution rejected");
      }
    } catch (err) {
      message.error(err.message || "Failed to resolve unit");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  renderStatusTag = (state) => {
    const colors = {
      COMPLETED: "green",
      REFUNDED: "orange",
      NEEDS_REVIEW: "volcano",
      PROCESSING: "processing",
      QUEUED: "purple",
      PENDING: "blue",
      QUOTED: "default",
      PARTIAL: "gold",
      FAILED: "red",
    };
    return <Tag color={colors[state] || "default"}>{state}</Tag>;
  };

  render() {
    const role = this.getRole();
    if (role !== "Admin") {
      return (
        <div style={{ padding: 24 }}>
          <Alert
            message="Access Denied"
            description="Access Denied: Main Admin role required to manage Khales card operations."
            type="error"
            showIcon
          />
        </div>
      );
    }

    const { isLoading, balance, orders, totalCount, page, limit } = this.state;

    const columns = [
      {
        title: "Order ID",
        dataIndex: "id",
        key: "id",
        render: (id) => <Text code copyable>{id}</Text>,
      },
      {
        title: "User ID",
        dataIndex: "user_id",
        key: "user_id",
      },
      {
        title: "Status",
        dataIndex: "state",
        key: "state",
        render: (st) => this.renderStatusTag(st),
      },
      {
        title: "Total Sale",
        key: "sale",
        render: (_, o) => `${o.totals_snapshot?.sale || "0.000"} LYD`,
      },
      {
        title: "Units",
        key: "units",
        render: (_, o) => (
          <span>
            {o.completed_units ?? o.totals_snapshot?.completed_units ?? 0} done / {o.pending_units ?? o.totals_snapshot?.pending_units ?? 0} pend / {o.refunded_units ?? o.totals_snapshot?.refunded_units ?? 0} ref
          </span>
        ),
      },
      {
        title: "Created At",
        key: "created_at",
        render: (_, o) => {
          const ts = o.createdAt || o.created_at;
          return ts ? new Date(ts).toLocaleString() : "N/A";
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, o) => (
          <Button size="small" type="primary" onClick={() => this.handleOpenDetail(o.id)}>
            View Details
          </Button>
        ),
      },
    ];

    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Khales Card Orders & Fulfillment</Title>

        {/* Balance Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="Khales Shared Balance (LYD)">
              <div style={{ fontSize: 24, fontWeight: "bold", color: "#1890ff" }}>
                {balance.lyd || balance.balance_lyd || "0.000"} LYD
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Khales Shared Balance (USD)">
              <div style={{ fontSize: 24, fontWeight: "bold", color: "#52c41a" }}>
                {balance.usd || balance.balance_usd || "0.000"} USD
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Select
                style={{ width: "100%" }}
                placeholder="Filter by Status"
                allowClear
                value={this.state.filterStatus}
                onChange={(val) => this.setState({ filterStatus: val })}
              >
                <Option value="QUOTED">QUOTED</Option>
                <Option value="QUEUED">QUEUED</Option>
                <Option value="PROCESSING">PROCESSING</Option>
                <Option value="PENDING">PENDING</Option>
                <Option value="NEEDS_REVIEW">NEEDS_REVIEW</Option>
                <Option value="COMPLETED">COMPLETED</Option>
                <Option value="PARTIAL">PARTIAL</Option>
                <Option value="FAILED">FAILED</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Input
                placeholder="User ID"
                value={this.state.filterUserId}
                onChange={(e) => this.setState({ filterUserId: e.target.value })}
              />
            </Col>
            <Col span={6}>
              <Button type="primary" onClick={() => this.loadOrders(1)}>
                Apply Filters
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  this.setState(
                    {
                      filterStatus: undefined,
                      filterUserId: "",
                      filterDateFrom: null,
                      filterDateTo: null,
                    },
                    () => this.loadOrders(1)
                  );
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: limit,
              total: totalCount,
              onChange: (p) => this.loadOrders(p),
            }}
          />
        </Spin>

        {/* Detail Modal */}
        {this.state.isDetailVisible && (
          <CardOrderDetails
            isVisible={this.state.isDetailVisible}
            order={this.state.selectedOrder}
            onClose={this.handleCloseDetail}
            onRecheckUnit={this.handleRecheck}
            onResolveUnit={this.handleResolve}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken:
    state.auth?.authToken ||
    state.auth?.token ||
    state.auth?.admin_token ||
    state.login?.authToken ||
    "",
  loginUserData: state.auth?.loginUserData || state.login?.loginUserData,
});

export default connect(mapStateToProps)(CardOrders);
